import React, { useEffect, useState } from 'react';
import oopsEmptyError from '../../../assets/icons/oops-empty-error.svg';
import './ErrorMessageView.scss';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import User from '../../../models/User';
import { useHistory } from 'react-router';

interface errorMessageProps {
    description: string;
    title: string;
    module?: string;
    buttonSecondaryText?: string;
    reload?: () => void;
    retryKey?: string;
    resetSignal?: number;
    isIndicator?: boolean;
}

const ErrorMessageView: React.FC<errorMessageProps> = ({
    description,
    title,
    module,
    buttonSecondaryText,
    reload,
    retryKey,
    resetSignal,
    isIndicator,
}) => {

    const { t } = useTranslation();
    const history = useHistory();
    const user = useSelector<any, User>(state => state.user);
    const fullName = [user?.name, user?.lastName].filter(Boolean).join(' ') || 'N/A';
    const storageKey = `error-message-retries-${retryKey || title}`;
    const [retryCount, setRetryCount] = useState(() => {
        if (typeof window === 'undefined') return 0;
        const stored = window.localStorage.getItem(storageKey);
        const parsed = parseInt(stored || '0', 10);
        return Number.isFinite(parsed) ? parsed : 0;
    });

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const stored = window.localStorage.getItem(storageKey);
        const parsed = parseInt(stored || '0', 10);
        setRetryCount(Number.isFinite(parsed) ? parsed : 0);
    }, [storageKey]);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        if (resetSignal === undefined) return;

        window.localStorage.removeItem(storageKey);
        setRetryCount(0);
    }, [resetSignal, storageKey]);


    const handleContactSupport = () => {
        const to = 'carterahelp@iadb.org';
        const subject = `${t('load_error_message_in')} ${module} - ${t('support_request_title')}`;
        const body = `${t('support_greeting')},

        ${t('issue_summary')}

        ${t('problem_details')}:
        - ${t('date_and_time')}: ${new Date().toLocaleString()}
        - ${t('user_issue')}: ${fullName}
        - ${t('module_issue')}: ${module}
        - ${t('displayed_message')}: "${title}"

        ${t('closing_thanks')}.`;
        const params = `subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        window.location.href = `mailto:${to}?${params}`;
    };

    const isContactMode = !isIndicator && retryCount >= 3;

    const handlePrimaryClick = () => {
        if (isContactMode) {
            handleContactSupport();
            return;
        }

        if (reload) {
            const nextCount = retryCount + 1;
            setRetryCount(nextCount);
            if (typeof window !== 'undefined') {
                window.localStorage.setItem(storageKey, String(nextCount));
            }
            reload();
        }
    };

    const toHome = () => {
        history.push('/home');
    }

    return (
        <div className='error-message-content'>
            <div className='styled-img'>
                <img src={oopsEmptyError} alt="img-message-error" />
            </div>
            <p className='title'>{title}</p>
            <p className='description'>{description}</p>

            {(reload || buttonSecondaryText) && (
                <div className='content-buttons'>
                    {isIndicator && (
                        <button className="btn btn-primary-outlined" type="button" onClick={toHome}>
                            {t('return_message')}
                        </button>
                    )}
                    {reload && (
                        <button
                            className="btn btn-primary"
                            type="button"
                            onClick={handlePrimaryClick}
                        >
                            {isContactMode ? t('contact_support') : t('retry_message')}
                        </button>
                    )}
                </div>
            )}
        </div>
    )
}

export default ErrorMessageView
